import { PROMPT_ENDPOINT } from '@Env/variables';
import {
  Comments,
  LibraryPrompt,
  UserPromptVersion,
  UserSavedPrompt,
} from '@Models/types';

const API_BASE_URL = PROMPT_ENDPOINT;

async function apiFetch<T>(endpoint: string, data: object = {}): Promise<T> {
  try {
    const response = await fetch(`${API_BASE_URL}/api${endpoint}`, {
      method: 'POST',
      headers: new Headers({
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + localStorage.getItem('accessToken'),
      }),
      body: JSON.stringify({
        ...data,
        email: localStorage.getItem('email') || '',
      }),
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw {
        status: response.status,
        message: responseData.message || 'An error occurred',
      };
    }

    return responseData;
  } catch (error) {
    console.error(`Error in ${endpoint}:`, error);
    throw error;
  }
}

export const getLibraryPrompts = async (): Promise<LibraryPrompt[]> => {
  return apiFetch<LibraryPrompt[]>('/getLibraryPrompts');
};

export const createLibraryPrompt = async (
  prompt: LibraryPrompt
): Promise<LibraryPrompt> => {
  return apiFetch<LibraryPrompt>('/createLibraryPrompt', { prompt });
};

export const importLibraryPrompt = async (
  prompts: LibraryPrompt[]
): Promise<{ message: string; inserted: UserSavedPrompt[] | null }> => {
  return apiFetch<{ message: string; inserted: UserSavedPrompt[] | null }>(
    '/importLibraryPrompts',
    { prompts }
  );
};

export const deleteLibraryPrompt = async (
  libraryId: string
): Promise<{ message: string }> => {
  return apiFetch<{ message: string }>('/deleteLibraryPrompts', { libraryId });
};

export const commentLibraryPrompt = async (
  promptId: string,
  comment: Comments
): Promise<{ message: string }> => {
  return apiFetch<{ message: string }>('/commentLibraryPrompts', {
    promptId,
    comment,
  });
};

export const voteLibraryPrompt = async (
  promptId: string,
  voteType: string
): Promise<{ message: string }> => {
  return apiFetch<{ message: string }>('/voteLibraryPrompts', {
    promptId,
    voteType,
  });
};

export const getUserPrompts = async (): Promise<UserSavedPrompt[]> => {
  return apiFetch<UserSavedPrompt[]>('/getUserPrompts');
};

export const createUserPrompt = async (
  prompt: Omit<UserSavedPrompt, 'id'>
): Promise<UserSavedPrompt> => {
  return apiFetch<UserSavedPrompt>('/createUserPrompt', { prompt });
};

export const addUserPromptVersion = async (
  promptId: string,
  prompt: Omit<UserPromptVersion, 'timestamp'>,
  version: string
): Promise<UserPromptVersion> => {
  return apiFetch<UserPromptVersion>('/createUserPromptVersion', {
    promptId,
    prompt,
    version,
  });
};

export const deleteUserPrompt = async (
  promptId: string
): Promise<{ message: string }> => {
  return apiFetch<{ message: string }>('/deleteUserPrompt', { promptId });
};

export const deleteUserPromptVersion = async (
  promptId: string,
  version: string
): Promise<{ message: string }> => {
  return apiFetch<{ message: string }>('/deleteUserPromptVersion', {
    promptId,
    version,
  });
};

export const renameUserPrompt = async (
  promptId: string,
  newName: string
): Promise<{ message: string }> => {
  return apiFetch<{ message: string }>('/renameUserPrompt', {
    promptId,
    newName,
  });
};

export const getLibraryPrompt = async (
  libraryId: string
): Promise<LibraryPrompt> => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/getLibraryPrompt?id=${libraryId}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error('Failed to fetch prompt data');
    }

    const data: LibraryPrompt = await response.json();
    return data;
  } catch (err) {
    console.error('Error fetching library prompt:', err);
    throw err;
  }
};
